import { useState, useEffect } from 'react';
import api from 'services/api';

/**
 * Hook personnalisé pour gérer les paramètres de Ville de Noël
 */
export const useVilleDeNoelSettings = () => {
  const [settings, setSettings] = useState(null);
  const [colors, setColors] = useState({});
  const [isActive, setIsActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Récupérer tous les paramètres
  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/ville-de-noel/settings');
      const data = response.data;
      
      setSettings(data.settings);
      setColors(data.colors);
      setIsActive(data.isActive);
      setError(null);
    } catch (err) {
      setError('Erreur lors de la récupération des paramètres Ville de Noël');
      console.error('Erreur useVilleDeNoelSettings:', err);
    } finally {
      setLoading(false);
    }
  };

  // Récupérer uniquement les couleurs (plus léger)
  const fetchColors = async () => {
    try {
      const response = await api.get('/ville-de-noel/colors');
      setColors(response.data);
      return response.data;
    } catch (err) {
      console.error('Erreur lors de la récupération des couleurs:', err);
      return {};
    }
  };

  // Récupérer le statut
  const fetchStatus = async () => {
    try {
      const response = await api.get('/ville-de-noel/status');
      const data = response.data;
      setIsActive(data.isActive);
      return data;
    } catch (err) {
      console.error('Erreur lors de la récupération du statut:', err);
      return { isActive: false };
    }
  };

  // Appliquer les couleurs CSS
  const applyColors = (colorVariables = colors) => {
    const root = document.documentElement;
    
    for (const [variable, value] of Object.entries(colorVariables)) {
      if (value) {
        root.style.setProperty(variable, value);
      }
    }
  };

  // Réinitialiser les couleurs aux valeurs par défaut
  const resetToDefaultColors = () => {
    const root = document.documentElement;
    
    const defaultVariables = {
      '--primary': 'hsla(201, 29%, 30%, 1)',
      '--primary-light': 'hsla(201, 29%, 45%, 1)',
      '--primary-ultra-light': 'hsla(201, 29%, 60%, 1)',
      '--primary-dark': 'hsla(201, 29%, 20%, 1)',
      '--primary-ultra-dark': 'hsla(201, 29%, 15%, 1)',

      '--secondary': 'hsla(193, 79%, 38%, 1)',
      '--secondary-light': 'hsla(193, 79%, 50%, 1)',
      '--secondary-ultra-light': 'hsla(193, 79%, 70%, 1)',
      '--secondary-dark': 'hsla(193, 79%, 30%, 1)',
      '--secondary-ultra-dark': 'hsla(193, 79%, 15%, 1)',

      '--services': 'hsla(201, 29%, 30%, 1)',
      '--services-light': 'hsla(201, 29%, 45%, 1)',
      '--services-ultra-light': 'hsla(201, 29%, 60%, 1)',
      '--services-dark': 'hsla(201, 29%, 20%, 1)',
      '--services-ultra-dark': 'hsla(201, 29%, 15%, 1)',

      '--carte': 'hsla(80, 100%, 37%, 1)',
      '--carte-light': 'hsla(80, 100%, 47%, 1)',
      '--carte-ultra-light': 'hsla(80, 100%, 70%, 1)',
      '--carte-dark': 'hsla(80, 100%, 27%, 1)',
      '--carte-ultra-dark': 'hsla(80, 100%, 15%, 1)',

      '--events': 'hsla(327, 100%, 45%, 1)',
      '--events-light': 'hsla(327, 100%, 55%, 1)',
      '--events-ultra-light': 'hsla(327, 100%, 75%, 1)',
      '--events-dark': 'hsla(327, 100%, 35%, 1)',
      '--events-ultra-dark': 'hsla(327, 100%, 25%, 1)',

      '--contact': 'hsla(201, 45%, 68%, 1)',
      '--contact-light': 'hsla(201, 45%, 78%, 1)',
      '--contact-ultra-light': 'hsla(201, 45%, 90%, 1)',
      '--contact-dark': 'hsla(201, 45%, 58%, 1)',
      '--contact-ultra-dark': 'hsla(201, 45%, 48%, 1)',
    };

    for (const [variable, value] of Object.entries(defaultVariables)) {
      root.style.setProperty(variable, value);
    }
  };

  // Charger les paramètres au montage du composant
  useEffect(() => {
    fetchSettings();
  }, []);

  return {
    // État
    settings,
    colors,
    isActive,
    loading,
    error,
    
    // Actions
    fetchSettings,
    fetchColors,
    fetchStatus,
    applyColors,
    resetToDefaultColors,
    
    // Helpers
    hasSnowEffect: settings?.enableSnowEffect || false,
    hasChristmasMusic: settings?.enableChristmasMusic || false,
    christmasMessage: settings?.christmasMessage || null,
    activationStartDate: settings?.activationStartDate || null,
    activationEndDate: settings?.activationEndDate || null,
  };
};

export default useVilleDeNoelSettings;
